/** Sprint 5 · Form Builder · 表單工作區（列出我可填寫的表單 → 填寫 / 檢視）。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import FunctionPageHeader from '../../../components/FunctionPageHeader';
import { listFillable, FormSummary } from './services/formBuilderService';

const FUNC = 'form_workspace';

const FormWorkspacePage: React.FC = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`formBuilder.${k}`, d);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showApiError } = useMessage();
  const canRead = hasPermission(FUNC, 'read');
  const [items, setItems] = useState<FormSummary[]>([]);

  const load = useCallback(async () => {
    try { setItems((await listFillable()).items); } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { if (!permLoading && canRead) load(); }, [permLoading, canRead, load]);

  if (permLoading) return null;
  if (!canRead) return <Box sx={{ p: 3 }}>{t('common.noPermission', '您沒有權限存取此功能')}</Box>;

  return (
    <Box sx={{ p: 2 }}>
      <FunctionPageHeader funcCode={FUNC} />
      <Typography variant="h6" sx={{ my: 1 }}>{tt('myForms', '我可填寫的表單')}</Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{tt('title', '標題')}</TableCell>
            <TableCell>{tt('desc', '說明')}</TableCell>
            <TableCell align="right">{tt('actions', '操作')}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={3}>
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{tt('noFillable', '目前沒有開放給你填寫的表單。')}</Typography>
            </TableCell></TableRow>}
            {items.map((f) => (
              <TableRow key={f.id} hover>
                <TableCell>{f.title}</TableCell>
                <TableCell><Typography variant="body2" color="text.secondary" noWrap>{f.description}</Typography></TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="contained" startIcon={<EditNoteIcon />}
                      onClick={() => nav(`/form_workspace/${f.id}/fill`)}>{tt('fill', '填寫')}</Button>
                    <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                      onClick={() => nav(`/form_workspace/${f.id}/view`)}>{tt('view', '檢視')}</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default FormWorkspacePage;
